import {ApiClient as Api} from '@hconnect/apiclient'
import {useNotification} from '@hconnect/uikit'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {Moment} from 'moment-timezone'
import {useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {type MonthToDatePlantPerformanceReviewKpisDto, PerformanceReviewQueryKey} from '../../types'

type DailyPerformanceKpisParams = {
  plantId: string
  requestedDate: Moment
}

export const usePerformanceReviewMonthlyKpis = (
  {plantId, ...searchParams}: DailyPerformanceKpisParams,
  options?: Omit<
    UseQueryOptions<MonthToDatePlantPerformanceReviewKpisDto, AxiosError>,
    'queryKey' | 'queryFn'
  >
) => {
  const {notify} = useNotification()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {requestedDate} = searchParams
  const params = {
    requestedYear: requestedDate.year(),
    requestedMonth: requestedDate.month() + 1 // Adjusting because Moment.js months are 0-indexed
  }

  const query = useQuery<MonthToDatePlantPerformanceReviewKpisDto, AxiosError>({
    queryKey: [PerformanceReviewQueryKey.MonthlyKpis, plantId, requestedDate.toISOString()],
    queryFn: async ({signal}) => {
      const path = `/kpi/actual-production-values/${plantId}/kpis/month-to-month/performance-review`
      const response = await Api.axiosInstance.get<MonthToDatePlantPerformanceReviewKpisDto>(path, {
        params,
        signal
      })
      return response.data
    },
    ...options
  })

  useEffect(() => {
    if (query.error) {
      notify('error', t('kpis.message.failedToFetchKpis'))
    }
  }, [query.error, notify, t])

  return query
}
